import { asLocale, INTL_LOCALE, type Locale } from "@elyxeer/ui";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { getItem, setItem, StorageKey } from "~/lib/storage";

/** Read the stored preference, falling back to the device language. */
export async function detectLocale(): Promise<Locale> {
  const stored = await getItem(StorageKey.Locale);
  if (stored) return asLocale(stored);
  return asLocale(typeof navigator !== "undefined" ? navigator.language : null);
}

export interface LocaleApi {
  locale: Locale;
  setLocale: (next: Locale) => Promise<void>;
  /** Locale-aware date formatting, so catalogues never carry a date format. */
  formatDate: (
    value: string | Date,
    options?: Intl.DateTimeFormatOptions,
  ) => string;
  /**
   * A grouped integer. Here rather than in a catalogue for the same reason dates
   * are: a thousands separator is a locale fact, not editorial copy.
   */
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  /** Takes a 0..1 ratio, because that is what the API returns. Null reads as "—". */
  formatPercent: (ratio: number | null, fractionDigits?: number) => string;
  /** Seconds as "48 s", "4 min 12 s", "1 h 06 min". Null reads as "—". */
  formatDuration: (seconds: number | null) => string;
}

/** What every formatter shows when the value is genuinely unknown. */
export const NO_VALUE = "—";

export function useLocale(): LocaleApi {
  const { i18n } = useTranslation();
  const locale = asLocale(i18n.language);

  const setLocale = useCallback(
    async (next: Locale) => {
      await i18n.changeLanguage(next);
      document.documentElement.lang = next;
      await setItem(StorageKey.Locale, next);
    },
    [i18n],
  );

  const formatDate = useMemo(() => {
    const defaultFormat: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "long",
      year: "numeric",
    };
    return (value: string | Date, options?: Intl.DateTimeFormatOptions) =>
      new Intl.DateTimeFormat(
        INTL_LOCALE[locale],
        options ?? defaultFormat,
      ).format(new Date(value));
  }, [locale]);

  // One Intl instance per locale, built here rather than per cell: a twenty-row
  // table would otherwise construct sixty formatters on every render.
  const formatNumber = useMemo(() => {
    const grouped = new Intl.NumberFormat(INTL_LOCALE[locale]);
    return (value: number, options?: Intl.NumberFormatOptions) =>
      options
        ? new Intl.NumberFormat(INTL_LOCALE[locale], options).format(value)
        : grouped.format(value);
  }, [locale]);

  const formatPercent = useMemo(() => {
    return (ratio: number | null, fractionDigits = 0) => {
      // Null means no denominator — an activation rate over nobody. Rendering it as
      // 0% would read as a failure rather than as a question that does not apply.
      if (ratio === null || Number.isNaN(ratio)) return NO_VALUE;
      return new Intl.NumberFormat(INTL_LOCALE[locale], {
        style: "percent",
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(ratio);
    };
  }, [locale]);

  const formatDuration = useMemo(() => {
    const unit = (value: number, name: "hour" | "minute" | "second") =>
      new Intl.NumberFormat(INTL_LOCALE[locale], {
        style: "unit",
        unit: name,
        unitDisplay: "short",
        maximumFractionDigits: 0,
      }).format(value);

    return (seconds: number | null) => {
      if (seconds === null || Number.isNaN(seconds)) return NO_VALUE;
      const total = Math.round(seconds);
      if (total < 60) return unit(total, "second");
      if (total < 3600) {
        const minutes = Math.floor(total / 60);
        const rest = total % 60;
        return rest === 0
          ? unit(minutes, "minute")
          : `${unit(minutes, "minute")} ${unit(rest, "second")}`;
      }
      const hours = Math.floor(total / 3600);
      const minutes = Math.round((total % 3600) / 60);
      return minutes === 0
        ? unit(hours, "hour")
        : `${unit(hours, "hour")} ${unit(minutes, "minute")}`;
    };
  }, [locale]);

  return {
    locale,
    setLocale,
    formatDate,
    formatNumber,
    formatPercent,
    formatDuration,
  };
}
